// translations/en.ts
import { TranslationKeys } from '../index';

export const en: TranslationKeys = {
  header: {
    tutorialButton: "Tutorials",
    currentLanguage: "Current"
  },
  hero: {
    title: "Play Sudoku Vs Now!",
    description: "Get ready for a Sudoku challenge like no other! Enjoy solo practice or face off against friends — download now and experience the excitement!",
    discordButton: 'Join our Discord',
    howToPlay: "How to Play",
    googlePlay: {
      topText: "Get it on",
      bottomText: "Google Play"
    },
    appStore: {
      topText: "Download on the",
      bottomText: "App Store"
    },
    botButtonShow: "Need Help? Ask Our Bot!",
    botButtonHide: "Hide Assistant"
  },
  footer: {
    followUs: "FOLLOW US ON",
    handle: "@SUDOKU.VS",
    copyright: "© 2024 Sudoku VS. All rights reserved."
  },
  beginnersGuide: {
    modalTitle: "BEGINNER'S GUIDE",
    originsTab: "SUDOKU ORIGINS",
    basicsTab: "SUDOKU BASICS",
    rulesTab: "VS GAME RULES",
    origins: {
      bulletOne: "Inspired by Latin squares; first published in 1979 by Howard Garns.",
      bulletTwo: "Became popular in Japan in the 1980s, renamed “Sudoku.” Gained global fame in the 2000s.",
      bulletThree: "9x9 grid with nine 3x3 subgrids; fill with 1 to 9 without repeats in rows, columns, or subgrids.",
      bulletFour: "Loved by millions, with many variations and new challenges.",
      gridSize: {
        four: "4 X 4",
        six: "6 X 6",
        nine: "9 X 9",
      }
    },
    basics: {
      fillMissing: "Fill in the missing number!",
      correct: "CORRECT!",
      incorrect: "INCORRECT",
      instructions: "Complete the 3X3 puzzle by entering the correct number for the highlighted space. Each row, column, and box must contain every number exactly once. Select the correct number. Choose wisely!",
      feedbackCorrect: "YOU SOLVED YOUR FIRST BOX!",
      feedbackIncorrect: "TRY AGAIN!",
      basicsStep2: {
        instructions: "Complete the 4X4 puzzle by selecting the correct number for the highlighted space. Each row, column, and box must contain every number exactly once. Select the correct number. Choose wisely!",
        feedbackCorrect: "YOU'RE ON A ROLL!"
      },
      basicsStep3: {
        instructions: "Complete the 4X4 puzzle by selecting the correct number for the highlighted space. Each row, column, and box must contain every number exactly once. Select the correct number. Choose wisely!",
        feedbackCorrect: "PERFECT SOLUTION!",
        feedbackIncorrect: "INCORRECT - TRY AGAIN!"
      },
      congratulations: {
        title: "Congratulations!",
        subtitle: "You've successfully completed the Sudoku basics tutorial! Your dedication and effort have paid off, and you're now equipped with the essential skills to tackle Sudoku puzzles with confidence.",
        nextStepsTitle: "Next Steps",
        nextStepsText: "Now it's time to put your skills to the test! Download the Sudoku game today and immerse yourself in a variety of puzzles. Whether you prefer a quick singles game or want to engage in some friendly competition, you can choose to play in single mode or vs mode against an opponent.",
        ctaHeader: "Up for a game?",
        ctaHighlight: "Download Now!",
        rulesTitle: "Master the Game Rules",
        rulesText: "Before you jump in, be sure to check out Sudoku VS: Game Rules. Understanding the rules and point system can give you an advantage when playing against opponents. This knowledge will enhance your experience and help you strategize effectively!"
      }
    },
    gameRules: {
      notification: {
        important: "Important: ",
        text: "Enable Notifications for Best Experience!"
      },
      rulesHeader: "Rules",
      rulesList: {
        rule1_pre: "Accept or create a ",
        rule1_strong: "friendly or random match",
        rule1_post: " invite.",
        rule2_pre: "Player is ",
        rule2_strong: "randomly selected to go first.",
        rule3: "Players take turns until the board is complete.",
        rule4_pre1: "Each ",
        rule4_strong1: "turn lasts 60 seconds,",
        rule4_pre2: " with a limit of ",
        rule4_strong2: "5 numbers per turn.",
        rule5_pre: "Only ",
        rule5_strong: "1 hint",
        rule5_post: " is allowed per turn.",
        rule6_pre: "The player with ",
        rule6_strong: "the highest score wins",
        rule6_post: " the game.",
        rule7_pre: "In the event of a ",
        rule7_strong_pre: "draw",
        rule7_pre2: ", the player with more inputs ",
        rule7_strong_post: "wins",
        rule7_post: " the game.",
        rule8: "If you do not end your turn within 24 hours, you automatically forfeit the game."
      },
      rulesWarning: "Play your turn in 24 hrs or you will automatically forfeit game",
      gameControls: {
        header: "Game Controls",
        list: {
          control1_pre: "Use the ",
          control1_strong: "Resign Flag",
          control1_post: " to forfeit the game.",
          control2_pre: "Use the ",
          control2_strong: "Eraser",
          control2_post: " to correct a mistake.",
          control3_pre: "Use ",
          control3_strong: "End Turn",
          control3_post: " if you have completed all your inputs before the timer runs out."
        }
      },
      scoring: {
        header: "Scoring",
        list: {
          score1_pre1: "Earn ",
          score1_strong1: "1 point",
          score1_pre2: " for ",
          score1_span: "correct",
          score1_post: " number placement.",
          score2_pre1: "Lose ",
          score2_strong1: "1 point",
          score2_pre2: " for each ",
          score2_span: "incorrect",
          score2_post: " number placement.",
          score3_pre1: "Earn ",
          score3_strong1: "5 points",
          score3_pre2: " for each row, column, and box ",
          score3_span: "solved",
          score3_post: "."
        }
      },
    },
    controller: {
      previous: "Previous",
      of: " of ",
      close: "Close",
      next: "Next",
      gameRules: "Game Rules",
    },
  },
  tutorials: {
    modalTitle: "Tutorials",
    tabs: {
      introduction: "Introduction",
      gettingStarted: "Getting Started",
      vsFriends: "Vs. Friends",
      howToPlay: "How to Play",
    },
    messages: {
      loading: "Loading tutorials...",
      error: "Error loading tutorials:",
      fallback: "Using fallback data...",
      empty: "No tutorials available for this section.",
    },
  },
  cookieConsent: {
    title: "Manage Consent Preferences",
    description: "We use cookies to enhance your Sudoku VS experience, analyze our traffic, and provide personalized ads.",
    acceptAll: "Accept All Cookies",
    confirmChoices: "Confirm Choices",
    performance: {
      title: "Performance Cookies",
      description: "Allows us to collect data to improve our game and services through usage analytics."
    },
    advertising: {
      title: "Advertising",
      adStorage: "Allows storage of advertising data on your device",
      adMeasurement: "Allows collection of data for advertising measurement",
      adPersonalization: "Allows us to show you personalized ads based on your interests"
    },
    landing: {
        title: "Cookie Notice",
        description: "We use cookies to enhance your Sudoku VS experience, analyze our traffic and provide personalized ads. You can set which cookies you want to allow before continuing.",
        acceptAll: "Accept all Cookies",
        customizeSettings: "Customize Settings",
        fineprint: "By clicking \"accept all cookies\", you agree<br />Sudoku VS can store cookies on your device"
    }
  },

    
  privacyPolicy: {
    modalTitle: "Privacy Policy",
    sections: {
      introduction: {
        title: "1. Introduction",
        p1: "Welcome to Sudoku VS. We are committed to protecting your privacy and ensuring you have a positive experience on our website. This Privacy Policy explains how we collect, use, and protect your information when you use our Sudoku game platform.",
        p2: "By using Sudoku VS, you agree to the collection and use of information in accordance with this policy."
      },
      informationWeCollect: {
        title: "2. Information We Collect",
        p1: "We collect different types of information to provide and improve our services:",
        list: {
          usageData: "Usage Data: We automatically collect information about how you interact with our game, including gameplay statistics, puzzle completion times, difficulty levels played, and game preferences.",
          deviceInfo: "Device Information: We may collect information about the device you use to access Sudoku VS, including device type, browser type, IP address, and operating system.",
          cookies: "Cookies and Tracking Data: We use cookies and similar tracking technologies to enhance your experience and collect analytics data."
        }
      },
      howWeUseInfo: {
        title: "3. How We Use Your Information",
        p1: "We use the collected information for the following purposes:",
        list: {
          gameImprovement: "Game Improvement: To analyze gameplay patterns and improve our Sudoku puzzles, difficulty algorithms, and overall user experience.",
          performanceAnalytics: "Performance Analytics: To monitor and analyze usage trends, measure the effectiveness of our services, and identify areas for enhancement.",
          personalization: "Personalization: To provide you with personalized game recommendations and tailored content based on your playing preferences.",
          technicalSupport: "Technical Support: To troubleshoot issues, fix bugs, and ensure the smooth operation of our platform."
        }
      },
      cookies: {
        title: "4. Cookies and Tracking Technologies",
        p1: "Sudoku VS uses cookies to enhance your experience. You have the option to manage your cookie preferences:",
        performanceCookies: {
          title: "Performance Cookies:",
          description: "These cookies allow us to collect data to improve our game and services through usage analytics. They help us understand how players interact with our puzzles, which features are most popular, and where we can make improvements. This data is essential for enhancing gameplay and creating better Sudoku experiences."
        },
        advertisingCookies: {
          title: "Advertising Cookies:",
          list: [
            "Allow storage of advertising data on your device",
            "Enable collection of data for advertising measurement and effectiveness",
            "Allow us to show you personalized ads based on your interests and gaming behavior"
          ]
        },
        p2: "You can control your cookie preferences through the cookie consent banner that appears when you first visit our website. You can change your settings at any time through your browser settings, though disabling certain cookies may affect website functionality."
      },
      thirdParty: {
        title: "5. Third-Party Services",
        p1: "Sudoku VS integrates with the following third-party services:",
        gemini: {
          title: "Google Gemini AI Assistant:",
          description: "Our help bot is powered by Google Gemini with a built-in knowledge base to assist you with game-related questions, provide Sudoku tips, and help troubleshoot issues. When you interact with the bot, your queries may be processed by Google's systems. Please refer to Google's Privacy Policy for information on how they handle data."
        },
        youtube: {
          title: "YouTube Embedded Videos:",
          description: "We use YouTube video overlays to provide tutorial content and gameplay guides directly within our website. When you interact with these videos, YouTube may collect data according to their privacy policy. You can watch videos without leaving Sudoku VS, but YouTube's tracking mechanisms may still apply."
        },
        advertising: {
          title: "Advertising Partners:",
          description: "We work with third-party advertising partners to display ads on our platform. These partners may use cookies and similar technologies to collect information about your browsing activity for personalized advertising purposes."
        }
      },
      dataSecurity: {
        title: "6. Data Security",
        p1: "We take the security of your data seriously and implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, please note that no method of transmission over the internet or electronic storage is 100% secure.",
        p2: "Our website uses industry-standard security protocols and is verified by GoDaddy security seal to ensure a safe browsing experience."
      },
      privacyRights: {
        title: "7. Your Privacy Rights",
        p1: "You have certain rights regarding your personal information:",
        list: {
          access: "Access: You can request information about the data we hold about you",
          correction: "Correction: You can request correction of inaccurate information",
          deletion: "Deletion: You can request deletion of your data, subject to legal obligations",
          optOut: "Opt-Out: You can opt out of personalized advertising through your cookie preferences",
          portability: "Data Portability: You can request a copy of your data in a portable format"
        }
      },
      childrenPrivacy: {
        title: "8. Children's Privacy",
        p1: "Sudoku VS is designed for general audiences. We do not knowingly collect personal information from children under the age of 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can take appropriate action."
      },
      changes: {
        title: "9. Changes to This Privacy Policy",
        p1: "We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the \"Last Updated\" date below.",
        p2: "We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information."
      },
      contact: {
        title: "10. Contact Us",
        p1: "If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:",
        email: "Email: privacy@sudoku.vs",
        website: "Website: www.sudoku.vs",
        p2: "We will respond to your inquiry as promptly as possible."
      }
    },
    footer: {
      lastUpdated: "Last Updated: October 10, 2025",
      effectiveDate: "Effective Date: October 10, 2025"
    }
  },
languages: {
    en: "English",
    es: "Español", 
    fr: "Français",
    hi: "हिन्दी",
    bn: "বাংলা",
    pt: "Português",
    it: "Italiano",
    ru: "Русский",
    ja: "日本語",
    ko: "한국어"
},
  copywright: {
    text: 'Copyright © 2025 Software Architects Ltd. All rights reserved.'
  }
};